import { useEffect, useMemo, useState } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { INGREDIENTS, HUBS } from "../data/ingredients";
import { slugifyIngredientName, useIngredientInfo } from "../lib/useIngredientInfo";
import { enrichIngredient } from "../lib/enrichIngredient";
import { bulkGenerateVoicelines, generateVoiceline } from "../lib/generateVoicelines";
import { CONFIGURED_VOICES, VOICELINES, VOICELINE_KEYS, resolveVoicelineText } from "../data/voicelines";
import { rememberBarcodeCorrection } from "../lib/barcodeCorrections";
import { useUserRecipes } from "../lib/useUserRecipes";
import { totalTimeMin, difficultyLabel } from "../data/recipes";
import { getWeatherFxOverride, setWeatherFxOverride } from "./WeatherFX";
import AdminDeadEndQueue from "./AdminDeadEndQueue";

// EditPackagingModal import removed — admin sizes catalog retired.

// AdminPanel — elevated-permissions inspector, scoped to profiles
// where role = 'admin' (see migration 0042). Mounted from Settings
// and only rendered when the viewer's own profile.role === 'admin'.
//
// FIRST-CUT SCOPE:
//   * USERS   — every profile row across all families. Id, name,
//               role, created_at. Lets the admin spot broken
//               onboarding (profile rows missing name) or orphaned
//               accounts.
//   * RECEIPTS — every receipt across all families. Store, date,
//                total, item_count, uploader, image_path. Tap a row
//                → surfaces the raw DB shape in a details panel
//                (no destructive ops in this cut — bypass writes
//                are too dangerous without explicit design).
//
// The RLS policies added in 0042 ONLY grant SELECT bypass to admins
// — UPDATE / DELETE still go through the standard self + family
// policies, so no write here can leak past what the admin's own
// family already had access to.
//
// Props:
//   userId    — viewer id (informational, not used for filtering)
//   onClose() — dismiss
export default function AdminPanel({ userId, onClose }) {
  const [tab, setTab] = useState("users"); // "users" | "receipts" | "enrichments" | "canonicals" | "recipes"

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000dd", zIndex: 345,
      display: "flex", alignItems: "flex-end",
      maxWidth: 480, margin: "0 auto",
    }}>
      <div style={{
        width: "100%", background: COLOR.ground,
        borderRadius: "20px 20px 0 0", padding: "18px 22px 36px",
        maxHeight: "94vh", overflowY: "auto",
        position: "relative",
      }}>
        <div style={{ width: 44, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 14px" }} />

        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 14,
            width: 32, height: 32,
            background: COLOR.deep, border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 16,
            fontFamily: FONT.mono, fontSize: 14,
            cursor: "pointer", zIndex: 1,
          }}
        >
          ✕
        </button>

        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.rose, letterSpacing: "0.12em", marginBottom: 6 }}>
          🛠 ADMIN TOOLS
        </div>
        <h2 style={{ fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic", color: COLOR.ink, fontWeight: 500, margin: 0, lineHeight: 1.2, marginBottom: 14 }}>
          Elevated permissions
        </h2>

        <div style={{ display: "flex", gap: 0, margin: "0 0 14px", padding: 4, background: COLOR.deep, border: `1px solid ${COLOR.edge}`, borderRadius: 12, overflowX: "auto" }}>
          {[
            { id: "users",       label: "USERS" },
            { id: "receipts",    label: "RECEIPTS" },
            { id: "enrichments", label: "ENRICHMENTS" },
            { id: "canonicals",  label: "CANONICALS" },
            { id: "deadend",     label: "DEAD-END UPCS" },
            { id: "baseline",    label: "BASELINE" },
            { id: "recipes",     label: "RECIPES" },
            { id: "voicelines",  label: "VOICELINES" },
            { id: "bgmusic",     label: "BG MUSIC" },
            { id: "xp",          label: "XP ECONOMY" },
            { id: "weatherfx",   label: "WEATHER FX" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1, padding: "8px",
                background: tab === t.id ? COLOR.edge : "transparent",
                border: "none", borderRadius: 8,
                fontFamily: FONT.mono, fontSize: 10, fontWeight: 600,
                color: tab === t.id ? COLOR.rose : COLOR.muted,
                cursor: "pointer", letterSpacing: "0.1em",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "users"       && <UsersList viewerId={userId} />}
        {tab === "receipts"    && <ReceiptsList viewerId={userId} />}
        {tab === "enrichments" && <PendingEnrichmentsList viewerId={userId} />}
        {tab === "canonicals"  && <CanonicalsList viewerId={userId} />}
        {tab === "deadend"     && <AdminDeadEndQueue viewerId={userId} />}
        {tab === "baseline"    && <BaselineCoverage viewerId={userId} />}
        {tab === "recipes"     && <RecipeSubmissionsList viewerId={userId} />}
        {tab === "voicelines"  && <VoicelinesPanel />}
        {tab === "bgmusic"     && <BgMusicTester />}
        {tab === "xp"          && <XpEconomyStats viewerId={userId} />}
        {tab === "weatherfx"   && <WeatherFxControls />}
      </div>
    </div>
  );
}

// ── Users list ────────────────────────────────────────────────────
function UsersList({ viewerId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error: err } = await supabase
        .from("profiles")
        .select("id, name, role, created_at, dietary, total_xp, last_cooked_date")
        .order("created_at", { ascending: false });
      if (!alive) return;
      if (err) { setError(err.message); setRows([]); return; }
      setRows(data || []);
    })();
    return () => { alive = false; };
  }, []);

  if (rows === null) return <Loading />;
  if (rows.length === 0) return <Empty msg={error ? `Load failed: ${error}` : "No profile rows."} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted, letterSpacing: "0.1em", padding: "0 4px 4px" }}>
        {rows.length} PROFILE{rows.length === 1 ? "" : "S"}
      </div>
      {rows.map(r => (
        <div
          key={r.id}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 12px",
            background: r.role === "admin" ? COLOR.roseDeep : COLOR.ground,
            border: `1px solid ${r.role === "admin" ? COLOR.roseDim : COLOR.edge}`,
            borderRadius: 10, textAlign: "left",
          }}
        >
          <div style={{ width: 32, height: 32, borderRadius: 16, background: COLOR.goldDeep, color: COLOR.gold, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT.serif, fontSize: 14, fontWeight: 500, flexShrink: 0 }}>
            {(r.name || "?")[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FONT.serif, fontSize: 13, color: COLOR.ink, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.name || "— no name —"}
              {r.id === viewerId && <span style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.gold, marginLeft: 6, letterSpacing: "0.08em" }}>(YOU)</span>}
            </div>
            <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, marginTop: 2, letterSpacing: "0.04em" }}>
              {r.id.slice(0, 8)}… · {new Date(r.created_at).toLocaleDateString()}
              {r.dietary ? ` · ${r.dietary.toUpperCase()}` : ""}
              {typeof r.total_xp === "number" ? ` · ${r.total_xp}XP` : ""}
            </div>
          </div>
          <span style={{
            fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
            color: r.role === "admin" ? COLOR.rose : COLOR.muted,
            background: r.role === "admin" ? COLOR.roseDeep : "transparent",
            border: `1px solid ${r.role === "admin" ? COLOR.roseDim : COLOR.border}`,
            padding: "2px 7px", borderRadius: 10, letterSpacing: "0.08em",
          }}>
            {(r.role || "user").toUpperCase()}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Receipts list ─────────────────────────────────────────────────
function ReceiptsList({ viewerId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error: err } = await supabase
        .from("receipts")
        .select("id, user_id, store_name, receipt_date, scanned_at, total_cents, item_count, image_path, created_at")
        .order("created_at", { ascending: false })
        .limit(500);
      if (!alive) return;
      if (err) { setError(err.message); setRows([]); return; }
      setRows(data || []);
    })();
    return () => { alive = false; };
  }, [version]);

  // Full-wipe delete: kills the pantry_items that landed from this
  // receipt, the storage image, and the receipt row itself — in that
  // order so nothing orphans. Requires migration 0049's admin-delete
  // policies across receipts / pantry_items / storage.objects.
  async function deleteReceipt(r) {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Delete receipt "${r.store_name || "no store"}"?\n\n` +
        `This removes the receipt row, every pantry_items row linked ` +
        `via source_receipt_id (${r.item_count ?? "?"} items), and the ` +
        `uploaded photo. Can't be undone.`
    );
    if (!ok) return;
    setBusy(r.id);
    try {
      const { error: itemsErr } = await supabase
        .from("pantry_items")
        .delete()
        .eq("source_receipt_id", r.id);
      if (itemsErr) throw itemsErr;
      if (r.image_path) {
        const { error: storageErr } = await supabase.storage
          .from("scans")
          .remove([r.image_path]);
        if (storageErr) console.warn("[receipt delete] image remove failed:", storageErr.message);
      }
      const { error: rowErr } = await supabase
        .from("receipts")
        .delete()
        .eq("id", r.id);
      if (rowErr) throw rowErr;
      setVersion(v => v + 1);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Delete failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) return <Loading />;
  if (rows.length === 0) return <Empty msg={error ? `Load failed: ${error}` : "No receipts yet."} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted, letterSpacing: "0.1em", padding: "0 4px 4px" }}>
        {rows.length} RECEIPT{rows.length === 1 ? "" : "S"} (NEWEST 500)
      </div>
      {rows.map(r => {
        const dateLabel = r.receipt_date
          ? new Date(`${r.receipt_date}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })
          : new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
        const isBusy = busy === r.id;
        return (
          <div
            key={r.id}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
              borderRadius: 10, textAlign: "left",
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>{r.image_path ? "📷" : "🧾"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: FONT.serif, fontSize: 13, color: COLOR.ink, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.store_name || "— no store —"}
              </div>
              <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, marginTop: 2, letterSpacing: "0.04em" }}>
                {dateLabel.toUpperCase()}
                {r.item_count != null ? ` · ${r.item_count} ITEMS` : ""}
                {` · ${r.user_id.slice(0, 8)}…`}
                {r.user_id === viewerId ? " (YOU)" : ""}
              </div>
            </div>
            {r.total_cents != null && (
              <span style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.leafMuted, letterSpacing: "0.04em" }}>
                ${(r.total_cents / 100).toFixed(2)}
              </span>
            )}
            <button
              onClick={() => deleteReceipt(r)}
              disabled={isBusy}
              style={adminBtnStyle(COLOR.roseDeep, COLOR.rose)}
            >
              {isBusy ? "…" : "DELETE"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Pending enrichments list ─────────────────────────────────────
// Every user-triggered AI enrichment lands in public.pending_ingredient_info
// with status='pending'. Admins see the queue here, eyeball the generated
// JSONB, optionally edit it, and promote approved rows into the canonical
// ingredient_info table with a clean ingredient_id (either minted fresh
// from the slug or mapped to an existing canonical the admin recognizes).
//
// RLS (migration 0047):
//   * admins SELECT all pending rows
//   * admins UPDATE pending rows (flip status, edit info)
//   * admins INSERT into ingredient_info (the approval destination) —
//     tightened from "any auth user" to is_admin() in the same migration
// Detect a "stub" pending row — one written by createPendingCanonicalFromScan
// (UPC scan that didn't bind to a registry canonical) rather than by
// EnrichmentButton (user explicitly requested AI enrichment). Stubs only
// carry tag-derived axes (category / tileId / typeId / subtype) and
// lack the AI-generated rich fields (description / prep / storage / etc).
//
// Heuristic: source === "scan_inferred" OR no description and _meta.source
// is missing/scan. Both branches are forgiving — if a future code path
// adds a different source token we'd rather treat it as a stub and offer
// enrichment than silently leave it without the affordance.
function isStubRow(row) {
  const info = row?.info || {};
  if (info.source === "scan_inferred") return true;
  const meta = info._meta || {};
  const metaSource = meta.source || "";
  if (metaSource.startsWith("scan")) return true;
  // Catch-all: no description AND no AI fingerprint → very likely a stub.
  if (!info.description && !meta.model && !meta.reviewed) return true;
  return false;
}

function PendingEnrichmentsList({ viewerId: _viewerId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null); // row id
  const [busy, setBusy] = useState(null); // row id currently being approved/rejected
  const [enriching, setEnriching] = useState(null); // row id currently being AI-enriched, or "ALL" for the bulk run
  // Admin-write side effect: refresh dbMap after every promote so the
  // running session's auto-link pool picks up the new canonical
  // without a full page reload.
  const { refreshDb, refreshPending } = useIngredientInfo();

  const stubRows = useMemo(() => (rows || []).filter(isStubRow), [rows]);

  async function enrichRow(row) {
    setEnriching(row.id);
    try {
      await enrichIngredient({ canonical_id: row.slug, source_name: row.source_name });
      await refreshPending?.();
      await reload();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Enrich failed: ${e.message || e}`);
    } finally {
      setEnriching(null);
    }
  }

  async function enrichAllStubs() {
    if (stubRows.length === 0) return;
    setEnriching("ALL");
    try {
      // Sequential — the edge function calls Anthropic per row; running in
      // parallel risks rate limits and the Admin queue is small enough
      // that serial latency is fine. Each row updates pending_ingredient_info
      // independently so a mid-run failure leaves successful rows enriched.
      for (const row of stubRows) {
        try {
          await enrichIngredient({ canonical_id: row.slug, source_name: row.source_name });
        } catch (e) {
          console.warn(`[admin] bulk enrich ${row.slug} failed:`, e?.message || e);
        }
      }
      await refreshPending?.();
      await reload();
    } finally {
      setEnriching(null);
    }
  }

  async function reload() {
    const { data, error: err } = await supabase
      .from("pending_ingredient_info")
      .select("id, user_id, slug, source_name, pantry_item_id, info, status, created_at")
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    if (err) { setError(err.message); setRows([]); return; }
    setRows(data || []);
  }

  useEffect(() => {
    reload();
  }, []);

  async function approve(row) {
    const suggested = row.slug;
    // eslint-disable-next-line no-alert
    const canonicalId = window.prompt(
      `Promote "${row.source_name}" to the canonical ingredient_info table.\n\n` +
        `Enter the canonical ingredient_id to use. Either mint a new one ` +
        `(slug from the source name is pre-filled) or map to an existing ` +
        `canonical id you recognize (e.g. "nori" for "Nori from the Japanese store").`,
      suggested,
    );
    if (!canonicalId) return;

    setBusy(row.id);
    try {
      // Stamp reviewed=true in _meta and upsert into ingredient_info.
      const reviewedInfo = {
        ...row.info,
        _meta: {
          ...(row.info?._meta || {}),
          reviewed: true,
          reviewed_by: _viewerId,
          reviewed_at: new Date().toISOString(),
          source: row.info?._meta?.source || "user_enrichment",
        },
      };
      const { error: upErr } = await supabase
        .from("ingredient_info")
        .upsert({ ingredient_id: canonicalId, info: reviewedInfo }, { onConflict: "ingredient_id" });
      if (upErr) throw upErr;

      // Slug reconciliation. When the admin promotes a pending row
      // to a DIFFERENT canonical id than its slug (e.g. slug =
      // "organic_mac_n_cheese", approved id = "mac_and_cheese"), any
      // pantry_items that were stamped with the pending slug need to
      // be migrated over — otherwise the row keeps pointing at a
      // canonical_id that has no ingredient_info row and the UI
      // reads as if the enrichment never approved. Same slug: no-op.
      if (row.slug && row.slug !== canonicalId) {
        const { error: rewireErr } = await supabase
          .from("pantry_items")
          .update({ canonical_id: canonicalId })
          .eq("canonical_id", row.slug);
        if (rewireErr) console.warn("[approve] pantry_items rewire failed:", rewireErr.message);
      }

      const { error: pErr } = await supabase
        .from("pending_ingredient_info")
        .update({ status: "approved", approved_canonical_id: canonicalId, info: reviewedInfo })
        .eq("id", row.id);
      if (pErr) throw pErr;

      // Notify the submitting user their draft is live.
      await supabase.from("notifications").insert({
        user_id: row.user_id,
        msg: `Your enrichment for ${row.source_name} was approved as "${canonicalId}"`,
        emoji: "✅",
        kind: "success",
      });

      await reload();
      await refreshDb?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Approve failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  async function reject(row) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Reject "${row.source_name}"? Optional reason:`, "");
    if (reason === null) return; // cancelled

    setBusy(row.id);
    try {
      const { error: pErr } = await supabase
        .from("pending_ingredient_info")
        .update({ status: "rejected", rejection_note: reason || null })
        .eq("id", row.id);
      if (pErr) throw pErr;

      await supabase.from("notifications").insert({
        user_id: row.user_id,
        msg: reason
          ? `Your enrichment for ${row.source_name} was rejected — ${reason}`
          : `Your enrichment for ${row.source_name} was rejected`,
        emoji: "❌",
        kind: "warn",
      });

      await reload();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Reject failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) return <Loading />;
  if (rows.length === 0) {
    return <Empty msg={error ? `Load failed: ${error}` : "No pending enrichments."} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "0 4px 4px",
      }}>
        <div style={{ flex: 1, fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted, letterSpacing: "0.1em" }}>
          {rows.length} PENDING · {stubRows.length} STUBS (UPC IMPORTS — NO AI YET)
        </div>
        {stubRows.length > 0 && (
          <button
            type="button"
            onClick={enrichAllStubs}
            disabled={enriching != null}
            style={adminBtnStyle("#2a2205", COLOR.gold)}
            title="Run AI enrichment on every stub row"
          >
            {enriching === "ALL" ? `ENRICHING ${stubRows.length}…` : `✨ ENRICH ALL STUBS (${stubRows.length})`}
          </button>
        )}
      </div>
      {rows.map(r => {
        const isOpen = expanded === r.id;
        const isBusy = busy === r.id;
        const isEnriching = enriching === r.id || enriching === "ALL";
        const isStub = isStubRow(r);
        const descrip = r.info?.description || "(no description)";
        return (
          <div key={r.id} style={{
            padding: "12px", background: COLOR.ground,
            border: `1px solid ${COLOR.edge}`, borderRadius: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: FONT.serif, fontSize: 14, color: COLOR.ink, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.source_name}
                </div>
                <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, marginTop: 2, letterSpacing: "0.04em" }}>
                  {r.slug} · {r.user_id.slice(0, 8)}… · {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
              {isStub && (
                <span title="UPC-import stub — no AI enrichment yet" style={{
                  fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
                  color: "#c8a8d4", background: "#1a1024",
                  border: "1px solid #3a2440",
                  padding: "2px 7px", borderRadius: 10, letterSpacing: "0.08em",
                }}>
                  STUB
                </span>
              )}
              <span style={{
                fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
                color: COLOR.gold, background: "#2a2205",
                border: "1px solid #5a4a0a",
                padding: "2px 7px", borderRadius: 10, letterSpacing: "0.08em",
              }}>
                PENDING
              </span>
            </div>
            <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim, lineHeight: 1.5, marginBottom: 10 }}>
              {String(descrip).slice(0, 180)}{String(descrip).length > 180 ? "…" : ""}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button
                onClick={() => setExpanded(isOpen ? null : r.id)}
                style={adminBtnStyle(COLOR.border, COLOR.dim)}
              >
                {isOpen ? "HIDE JSON" : "VIEW JSON"}
              </button>
              {isStub && (
                <button
                  onClick={() => enrichRow(r)}
                  disabled={isEnriching || isBusy}
                  style={adminBtnStyle("#2a2205", COLOR.gold)}
                  title="Run AI enrichment to fill in description / prep / storage / etc"
                >
                  {isEnriching ? "ENRICHING…" : "✨ AI ENRICH"}
                </button>
              )}
              <button
                onClick={() => approve(r)}
                disabled={isBusy || isEnriching}
                style={adminBtnStyle("#0a2a12", COLOR.leafMuted)}
              >
                {isBusy ? "…" : "APPROVE"}
              </button>
              <button
                onClick={() => reject(r)}
                disabled={isBusy || isEnriching}
                style={adminBtnStyle(COLOR.roseDeep, COLOR.rose)}
              >
                REJECT
              </button>
            </div>
            {isOpen && (
              <pre style={{
                marginTop: 10, padding: 10, background: COLOR.deep,
                border: `1px solid ${COLOR.edge}`, borderRadius: 8,
                fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim,
                overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
                maxHeight: 400,
              }}>
                {JSON.stringify(r.info, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}

// CANONICALS — the full registry, bundled + user-created, with live
// usage counts so Trevor can see which ones are in circulation.
// Custom canonicals (slugs created on-the-fly from ItemCard) get the
// same accept/reject flow the ENRICHMENTS tab uses:
//   APPROVE — upserts an ingredient_info stub so the slug becomes an
//             "official" canonical (the CANONICAL chip stops showing
//             the · PENDING marker).
//   REJECT  — nullifies canonical_id on every pantry_items row that
//             points at the slug and removes any ingredient_info stub
//             (so the slug falls out of circulation entirely).
//   RENAME  — rewrites the slug in-place across every pantry_items
//             row, carrying existing ingredient_info + review rows to
//             the new id.
// Bundled canonicals (src/data/ingredients.js) are read-only here —
// renaming those lives in code.
function CanonicalsList({ viewerId }) {
  const [usage, setUsage] = useState(null); // Map<canonical_id, count>
  const [approvedIds, setApprovedIds] = useState(null); // Set<slug>
  const [overrides, setOverrides] = useState(null); // Map<slug, display_name>
  const [parentIds, setParentIds] = useState(null); // Map<slug, parentId>  — hub assignment from ingredient_info.info.parentId (v0.13.0)
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);    // canonical_id mid-op
  const [version, setVersion] = useState(0); // cheap refresh trigger
  // Per-canonical row drilldown. Clicking the ×N usage badge loads
  // the actual pantry_items rows attached to that canonical so the
  // admin can rewire a SPECIFIC row (the mistagged Pepsi) without
  // rewriting every row on the slug the way the blanket RENAME does.
  // rowsFor is the canonical_id currently expanded (null = collapsed).
  // rowsCache lazily memoizes the fetched list by slug — invalidated
  // per-slug after each rewire so the row we just moved away drops
  // out of the visible list.
  const [rowsFor, setRowsFor] = useState(null);
  const [rowsCache, setRowsCache] = useState(new Map());
  // Reverse index of the Tier-1 learned tag map: canonical_id →
  // [off_tag, ...]. Admins can add/remove tags per canonical to
  // teach the resolver explicitly (without waiting for a user
  // correction to seed a hint). The value is loaded once per
  // CanonicalsList reload cycle so the chips under each row stay in
  // sync with writes. Empty when migration 0123 isn't applied yet.
  const [offTagsByCanonical, setOffTagsByCanonical] = useState(new Map());
  // Packaging editor — opens a ModalSheet over the admin panel,
  // seeded with the canonical's saved sizes + parentId. Null =
  // closed; object = the row being edited.
  // editPackagingFor state retired — no admin packaging surface.
  // Refresh the session-level dbMap after every approve/reject/rename
  // so the scan-time auto-star-link and the LinkIngredient picker
  // pick up the admin's changes without a page reload.
  const { refreshDb } = useIngredientInfo();

  async function reload() {
    const [{ data: pantryRows, error: pantryErr }, { data: infoRows, error: infoErr }] = await Promise.all([
      supabase
        .from("pantry_items")
        .select("canonical_id")
        .not("canonical_id", "is", null),
      supabase
        .from("ingredient_info")
        .select("ingredient_id, info"),
    ]);
    if (pantryErr) { setError(pantryErr.message); setUsage(new Map()); }
    else {
      const m = new Map();
      for (const r of (pantryRows || [])) {
        const id = r.canonical_id;
        if (!id) continue;
        m.set(id, (m.get(id) || 0) + 1);
      }
      setUsage(m);
    }
    if (infoErr) {
      // Non-fatal — without approvedIds every custom row just reads as PENDING.
      setApprovedIds(new Set());
      setOverrides(new Map());
      setParentIds(new Map());
    } else {
      setApprovedIds(new Set((infoRows || []).map(r => r.ingredient_id).filter(Boolean)));
      const om = new Map();
      const pm = new Map();
      for (const r of (infoRows || [])) {
        const dn = r.info?.display_name;
        if (r.ingredient_id && dn && typeof dn === "string") om.set(r.ingredient_id, dn);
        const pid = r.info?.parentId;
        if (r.ingredient_id && pid && typeof pid === "string") pm.set(r.ingredient_id, pid);
      }
      setOverrides(om);
      setParentIds(pm);
    }
    // Tier-1 learned tag map (migration 0123). Reverse-index into
    // canonical_id → [off_tag, ...] so each canonical card can list
    // its mapped tags inline. Silent no-op on envs without 0123.
    const { data: tagRows, error: tagErr } = await supabase
      .from("off_category_tag_canonicals")
      .select("off_tag, canonical_id");
    if (tagErr) {
      if (tagErr.code !== "42P01") {
        console.warn("[admin] off tag map load:", tagErr.message);
      }
      setOffTagsByCanonical(new Map());
    } else {
      const tm = new Map();
      for (const r of (tagRows || [])) {
        if (!r?.canonical_id || !r?.off_tag) continue;
        const arr = tm.get(r.canonical_id) || [];
        arr.push(r.off_tag);
        tm.set(r.canonical_id, arr);
      }
      // Sort tags alphabetically so chip order is predictable.
      for (const arr of tm.values()) arr.sort();
      setOffTagsByCanonical(tm);
    }
  }

  useEffect(() => { reload(); }, [version]);

  // Merge the bundled registry with any canonical_ids that show up in
  // use but aren't in the registry (user-created slugs). Every row
  // carries source + status flags so the UI can gate actions.
  const rows = useMemo(() => {
    if (!usage || !approvedIds || !overrides || !parentIds) return null;
    const seen = new Set();
    const bundled = INGREDIENTS.map(i => {
      seen.add(i.id);
      const override = overrides.get(i.id);
      return {
        id: i.id,
        name: override || i.name,
        bundledName: i.name,
        hasOverride: !!override,
        emoji: i.emoji || "🥫",
        category: i.category || null,
        source: "bundled",
        count: usage.get(i.id) || 0,
        approved: true,
        // Bundled canonicals carry parentId from the registry; the
        // dbMap override only applies to user-created canonicals.
        parentId: i.parentId || null,
      };
    });
    const custom = [];
    for (const [id, count] of usage.entries()) {
      if (seen.has(id)) continue;
      custom.push({
        id,
        name: id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
        emoji: "✨",
        category: null,
        source: "custom",
        count,
        approved: approvedIds.has(id),
        parentId: parentIds.get(id) || null,
      });
    }
    // Orphan approvals — ingredient_info rows for a slug that's not in
    // the bundled registry and isn't currently in use. Still worth
    // showing so they can be rejected.
    for (const id of approvedIds) {
      if (seen.has(id)) continue;
      if (usage.has(id)) continue;
      custom.push({
        id,
        name: id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
        emoji: "✨",
        category: null,
        source: "custom",
        count: 0,
        approved: true,
        parentId: parentIds.get(id) || null,
      });
    }
    return [...custom, ...bundled];
  }, [usage, approvedIds, overrides, parentIds]);

  // Set (or clear) the parentId / hub assignment on a user-created
  // canonical. Bundled canonicals get their parentId from the registry,
  // not from admin writes, so this action is scoped to isCustom rows.
  async function setParent(row, newParentId) {
    if (row.source !== "custom") return;
    setBusy(row.id);
    try {
      // Read-modify-write on the JSONB info field so we preserve
      // anything else already there (packaging, description, etc.).
      const { data: existing } = await supabase
        .from("ingredient_info")
        .select("info")
        .eq("ingredient_id", row.id)
        .maybeSingle();
      const prev = existing?.info || {};
      const next = { ...prev };
      if (newParentId) next.parentId = newParentId;
      else delete next.parentId;
      // Stamp _meta so the row passes isMeaningfullyEnriched now
      // that it has real data (parentId qualifies as meaningful).
      next._meta = {
        ...(prev._meta || {}),
        reviewed: true,
        reviewed_by: viewerId || null,
        reviewed_at: new Date().toISOString(),
        source: "admin_set_parent",
        stub: false,
      };
      const { error: upErr } = await supabase
        .from("ingredient_info")
        .upsert({ ingredient_id: row.id, info: next }, { onConflict: "ingredient_id" });
      if (upErr) throw upErr;
      setVersion(v => v + 1);
      refreshDb?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Couldn't set parent: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  async function renameBundled(row) {
    // eslint-disable-next-line no-alert
    const next = window.prompt(
      `Rename "${row.bundledName || row.name}".\n\n` +
        `This is a BUNDLED canonical — we can't change the slug ` +
        `("${row.id}") since it's referenced in code. Instead, we'll ` +
        `write a display-name override into ingredient_info.\n\n` +
        `Leave blank to clear any existing override.`,
      row.name,
    );
    if (next === null) return;
    const trimmed = next.trim();

    setBusy(row.id);
    try {
      const { data: existing } = await supabase
        .from("ingredient_info")
        .select("info")
        .eq("ingredient_id", row.id)
        .maybeSingle();
      const prevInfo = (existing && existing.info && typeof existing.info === "object") ? existing.info : {};
      const nextInfo = { ...prevInfo };
      if (trimmed) {
        nextInfo.display_name = trimmed;
      } else {
        delete nextInfo.display_name;
      }
      const { error: upErr } = await supabase
        .from("ingredient_info")
        .upsert({ ingredient_id: row.id, info: nextInfo }, { onConflict: "ingredient_id" });
      if (upErr) throw upErr;
      setVersion(v => v + 1); refreshDb?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Rename failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  async function renameCustom(row) {
    // eslint-disable-next-line no-alert
    const next = window.prompt(
      `Rename "${row.name}".\n\n` +
        `Every pantry_items.canonical_id set to "${row.id}" will be rewritten. ` +
        `Type the new display name — we'll slugify it automatically.`,
      row.name,
    );
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) return;
    const newSlug = slugifyIngredientName(trimmed);
    if (!newSlug || newSlug === row.id) return;

    setBusy(row.id);
    try {
      // Carry any approval stub to the new slug. Delete the old row
      // after inserting the new one so the transition is no-approval-
      // gap from the user's perspective.
      if (row.approved) {
        const { data: existing } = await supabase
          .from("ingredient_info")
          .select("info")
          .eq("ingredient_id", row.id)
          .maybeSingle();
        if (existing) {
          await supabase
            .from("ingredient_info")
            .upsert({ ingredient_id: newSlug, info: existing.info }, { onConflict: "ingredient_id" });
          await supabase
            .from("ingredient_info")
            .delete()
            .eq("ingredient_id", row.id);
        }
      }
      const { error: upErr } = await supabase
        .from("pantry_items")
        .update({ canonical_id: newSlug })
        .eq("canonical_id", row.id);
      if (upErr) throw upErr;
      // Also carry ingredient_id (legacy scalar — some read paths
      // still consult it as the primary tag) and barcode_identity_
      // corrections so the UPC → canonical memory survives a rename.
      // Without this carry, every previously-taught UPC would stay
      // pointed at the old slug; next scan would try to open a
      // canonical that no longer exists and fall back to fuzzy
      // matching all over again.
      await supabase
        .from("pantry_items")
        .update({ ingredient_id: newSlug })
        .eq("ingredient_id", row.id);
      await supabase
        .from("barcode_identity_corrections")
        .update({ canonical_id: newSlug })
        .eq("canonical_id", row.id);
      // off_category_tag_canonicals (migration 0123) also needs to
      // follow the canonical through a rename — otherwise every
      // learned tag would still point at the old slug and Tier-1
      // resolution would try to open a canonical that's been renamed
      // away. Non-fatal on older envs that haven't applied 0123.
      const { error: tagMapErr } = await supabase
        .from("off_category_tag_canonicals")
        .update({ canonical_id: newSlug })
        .eq("canonical_id", row.id);
      if (tagMapErr && tagMapErr.code !== "42P01") {
        console.warn("[admin-rename] off_category_tag_canonicals carry:", tagMapErr.message);
      }
      // user_scan_corrections is the family-scoped mirror; same carry.
      // Non-fatal if the table doesn't exist on older envs — we just
      // log and move on.
      const { error: userCorrErr } = await supabase
        .from("user_scan_corrections")
        .update({ canonical_id: newSlug })
        .eq("canonical_id", row.id);
      if (userCorrErr && userCorrErr.code !== "42P01") {
        console.warn("[admin-rename] user_scan_corrections carry:", userCorrErr.message);
      }
      setVersion(v => v + 1); refreshDb?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Rename failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  async function approveCustom(row) {
    setBusy(row.id);
    try {
      // Stub metadata — empty info + _meta.reviewed so the record
      // exists. The enrichment pipeline can fill the body later; for
      // the admin portal this is purely an approval flag.
      const stub = {
        _meta: {
          reviewed: true,
          reviewed_by: viewerId || null,
          reviewed_at: new Date().toISOString(),
          source: "admin_canonical_approve",
        },
      };
      const { error: upErr } = await supabase
        .from("ingredient_info")
        .upsert({ ingredient_id: row.id, info: stub }, { onConflict: "ingredient_id" });
      if (upErr) throw upErr;
      setVersion(v => v + 1); refreshDb?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Approve failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  // Expand/collapse the pantry-row drilldown for a canonical. On first
  // expand we fetch the rows (lazy, cached in rowsCache) so idle
  // canonicals don't pay the query cost. Scoped to canonical_id; we
  // pull brand + name so the admin can visually identify which row
  // is the mistagged one (brand="PEPSI" + name="Soda Pop" on a sugar
  // canonical jumps out immediately).
  async function toggleRows(canonicalId) {
    if (rowsFor === canonicalId) {
      setRowsFor(null);
      return;
    }
    setRowsFor(canonicalId);
    if (rowsCache.has(canonicalId)) return;
    const { data, error: pErr } = await supabase
      .from("pantry_items")
      .select("id, name, brand, emoji, amount, unit, user_id, canonical_id")
      .eq("canonical_id", canonicalId)
      .order("name", { ascending: true })
      .limit(100);
    if (pErr) {
      // eslint-disable-next-line no-alert
      window.alert(`Couldn't load rows: ${pErr.message}`);
      return;
    }
    setRowsCache(prev => new Map(prev).set(canonicalId, data || []));
  }

  // Per-row rewire — move ONE pantry_items row off its current
  // canonical and onto a (possibly new) target canonical. The key
  // difference vs renameCustom: that one rewrites every row on the
  // slug wholesale, which is catastrophic when only one row is
  // mistagged (classic misfire: a "SUGAR FREE" Pepsi landed on the
  // sugar canonical, the other sugar rows are legit and must stay).
  //
  // "Completely drop the old identity" means every column on the
  // pantry row that was populated from (or validated against) the old
  // canonical gets cleared in the same UPDATE:
  //   - canonical_id      → new slug
  //   - ingredient_id     → new slug (legacy dual column — reading
  //                          code still consults it as the primary
  //                          scalar in some paths; out-of-sync here
  //                          is how "ghost sugar" lingers).
  //   - cut               → null    (meat-only axis; invalid unless
  //                          the target canonical is also meat — we
  //                          clear unconditionally so the admin can
  //                          re-pick on the new canonical's
  //                          vocabulary rather than carrying a stale
  //                          "chicken breast" onto a beef row).
  //   - state             → null    (per-canonical state vocab; a
  //                          "SWEETENED / ZERO SUGAR" tag inherited
  //                          from the sugar misfire has no meaning on
  //                          a soda_pop row — clear and let the user
  //                          re-tag from the new canonical's options).
  //   - nutrition_override→ null    (tier-1 nutrition blob computed
  //                          against the old canonical's per-unit
  //                          shape; keeping it would leak old-
  //                          canonical macros into the new identity's
  //                          tally. Force a re-resolve.)
  //   - components        → filter out the old slug if it appears
  //                          there (composed rows that explicitly
  //                          listed the wrong canonical in their
  //                          composition stack — rare, but leaving it
  //                          is a leak).
  //
  // Columns deliberately LEFT ALONE because they're orthogonal to
  // canonical identity: brand, name, emoji, attributes, barcode_upc,
  // package_amount/package_unit, count_weight_g, location, category.
  // Those track the physical item, not the classification.
  async function rewireRow(pantryRow, oldSlug) {
    // eslint-disable-next-line no-alert
    const next = window.prompt(
      `Rewire this pantry row off "${oldSlug}".\n\n` +
        `Row: ${pantryRow.brand ? pantryRow.brand + " · " : ""}${pantryRow.name || "(unnamed)"}\n\n` +
        `Type the NEW canonical display name. If the slug doesn't exist ` +
        `yet, we'll create it as an approved synthetic so it stops reading ` +
        `as PENDING.\n\n` +
        `Every canonical-tied field on this row (cut, state, ` +
        `nutrition_override, ingredient_id, components entries on the ` +
        `old slug) will be fully dropped — brand, name, emoji, and ` +
        `package metadata stay.`
    );
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) return;
    const newSlug = slugifyIngredientName(trimmed);
    if (!newSlug || newSlug === oldSlug) return;

    // Optional emoji — prompt only when we're creating a brand-new
    // synthetic (existing bundled + approved targets already have one
    // from the registry / admin edit). Blank-or-cancel keeps the stub
    // emoji-less and lets the ✨ fallback apply, which is still
    // better than the previous "sparkles forever" because enrichment
    // below will usually produce a better one within seconds.
    const isBundled = INGREDIENTS.some(i => i.id === newSlug);
    const isApproved = approvedIds?.has(newSlug);
    let emoji = null;
    if (!isBundled && !isApproved) {
      // eslint-disable-next-line no-alert
      const rawEmoji = window.prompt(
        `Emoji for "${trimmed}"?\n\nSingle emoji like 🥤 / 🍺 / 🍕. ` +
          `Leave blank and we'll use a ✨ placeholder until enrichment ` +
          `fills one in.`,
        "",
      );
      if (rawEmoji === null) return;   // admin cancelled the whole op
      emoji = rawEmoji.trim() || null;
    }

    setBusy(`row:${pantryRow.id}`);
    try {
      if (!isBundled && !isApproved) {
        // Rich stub so the synthetic is searchable in LinkIngredient
        // (display_name feeds the picker's fuzzy matcher — without it
        // the slug "soda_pop" didn't match the query "Soda Pop"),
        // renderable on ItemCard + Kitchen tiles (emoji populated so
        // the fallback ✨ doesn't leak), and not grouped under a
        // random hub (no parentId — let the admin set it explicitly
        // via GROUP UNDER, which is safer than auto-inferring since
        // the slug might not match any keyword bucket cleanly).
        const stub = {
          display_name: trimmed,
          ...(emoji ? { emoji } : {}),
          _meta: {
            reviewed: true,
            reviewed_by: viewerId || null,
            reviewed_at: new Date().toISOString(),
            source: "admin_rewire",
          },
        };
        const { error: infoErr } = await supabase
          .from("ingredient_info")
          .upsert({ ingredient_id: newSlug, info: stub }, { onConflict: "ingredient_id" });
        if (infoErr) throw infoErr;
        // Fire-and-forget AI enrichment. Fills in density / category /
        // nutrition / sourcing / substitutions so the synthetic stops
        // reading as a bare stub and starts rendering the same
        // cooking metadata bundled canonicals have. Same pattern as
        // LinkIngredient's createNewFromQuery — no await, network
        // slowness doesn't block the rewire.
        enrichIngredient({ canonical_id: newSlug, source_name: trimmed })
          .then(() => { refreshDb?.(); })
          .catch(err => {
            console.warn("[admin-rewire] enrichment failed for", newSlug, err?.message);
          });
      }
      // Read the row's components + barcode_upc + attributes so we
      // can (a) scrub any mention of the old slug from components
      // before the UPDATE lands, (b) persist a
      // barcode_identity_corrections entry below if this row came
      // from a scan, and (c) seed the Tier-1 learned tag map from
      // the categoryHints that the scan originally carried (stored
      // inside attributes.categoryHints per the scan-flow). Single
      // read is cheap and avoids a race with whatever the client
      // last wrote.
      const { data: curRow, error: readErr } = await supabase
        .from("pantry_items")
        .select("ingredients, components, ingredient_ids, barcode_upc, attributes")
        .eq("id", pantryRow.id)
        .maybeSingle();
      if (readErr) throw readErr;
      // Migration 0145 renamed the column. Read from any of the three
      // historical names so this admin sweep keeps working against
      // databases at any point in the rename window.
      const rawIngredients = Array.isArray(curRow?.ingredients)
        ? curRow.ingredients
        : Array.isArray(curRow?.components)
          ? curRow.components
          : Array.isArray(curRow?.ingredient_ids) ? curRow.ingredient_ids : null;
      const scrubbed = rawIngredients
        ? rawIngredients.filter(x => x && x !== oldSlug)
        : null;
      const ingredientsPatch = scrubbed == null
        ? {}
        : scrubbed.length === 0
          ? { ingredients: null }
          : { ingredients: scrubbed };

      const { error: upErr } = await supabase
        .from("pantry_items")
        .update({
          canonical_id:       newSlug,
          ingredient_id:      newSlug,
          cut:                null,
          state:              null,
          nutrition_override: null,
          ...ingredientsPatch,
        })
        .eq("id", pantryRow.id);
      if (upErr) throw upErr;

      // Teach the barcode resolver. If the pantry row we just rewired
      // came from a scan (barcode_upc is set), the admin has now given
      // us the authoritative canonical for that UPC — future scans of
      // the same barcode should skip OFF's productName fuzzy-match and
      // land on this slug. Mirrors the same hook the scan-flow
      // CanonicalCreatePrompt uses at Kitchen.jsx:966. Admins write to
      // the GLOBAL barcode_identity_corrections table (all users benefit)
      // rather than the family-scoped user_scan_corrections.
      const scannedUpc = curRow?.barcode_upc || null;
      const scannedHints = Array.isArray(curRow?.attributes?.categoryHints)
        ? curRow.attributes.categoryHints
        : null;
      if (scannedUpc && viewerId) {
        rememberBarcodeCorrection({
          userId:        viewerId,
          isAdmin:       true,
          barcodeUpc:    scannedUpc,
          canonicalId:   newSlug,
          ingredients: [newSlug],
          categoryHints: scannedHints,   // seeds the Tier-1 tag map
          ...(emoji ? { emoji } : {}),
        })
          .then(res => {
            if (res?.error) {
              console.warn("[admin-rewire] barcode correction write failed:", res.error.message);
            }
          })
          .catch(err => {
            console.warn("[admin-rewire] barcode correction threw:", err?.message);
          });
      }
      setRowsCache(prev => {
        const m = new Map(prev);
        m.delete(oldSlug);
        m.delete(newSlug);
        return m;
      });
      setVersion(v => v + 1); refreshDb?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Rewire failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  // Add one-or-more OFF category tag → this canonical mappings.
  // Admin-only; surfaces the Tier-1 learned tag map directly so you
  // don't have to scan-and-correct a product to seed it. Comma-
  // separated input keeps the interaction dense — typical use is
  // bulk-add after renaming a canonical ("soft_drink: sodas, colas,
  // carbonated-drinks, diet-sodas" in one shot).
  async function addOffTags(row) {
    // eslint-disable-next-line no-alert
    const raw = window.prompt(
      `Add OFF category tags to "${row.name}" (${row.id}).\n\n` +
        `Comma-separated. OFF slug form only — dashes not spaces ` +
        `(e.g. "sodas, colas, carbonated-drinks, diet-sodas").\n\n` +
        `Any fresh scan whose categoryHints contain one of these ` +
        `will Tier-1 match this canonical at "exact" confidence, no ` +
        `user correction needed. If the same tag already maps to ` +
        `another canonical, the most recent write wins — so adding ` +
        `"sodas" here steals the mapping from whatever had it before.`
    );
    if (!raw) return;
    const tags = Array.from(new Set(
      raw.split(",")
        .map(t => t.trim().toLowerCase().replace(/\s+/g, "-"))
        .filter(Boolean)
    ));
    if (tags.length === 0) return;
    setBusy(`tags:${row.id}`);
    try {
      const now = new Date().toISOString();
      for (const tag of tags) {
        const { error } = await supabase
          .from("off_category_tag_canonicals")
          .upsert(
            {
              off_tag:          tag,
              canonical_id:     row.id,
              correction_count: 1,
              last_used_at:     now,
              created_by:       viewerId || null,
            },
            { onConflict: "off_tag" },
          );
        if (error) {
          if (error.code === "42P01") {
            // eslint-disable-next-line no-alert
            window.alert(
              "Tag map table not found. Migration 0123 hasn't been applied to this database yet — run `supabase db push` and retry."
            );
            return;
          }
          console.warn(`[admin] add off_tag ${tag}:`, error.message);
        }
      }
      setVersion(v => v + 1);
    } finally {
      setBusy(null);
    }
  }

  // Remove a single tag → canonical mapping. Because the map's key
  // is off_tag alone (one canonical per tag globally), this is a
  // straight DELETE by primary key — no cascade needed.
  async function removeOffTag(tag) {
    const trimmed = String(tag || "").trim().toLowerCase();
    if (!trimmed) return;
    setBusy(`untag:${trimmed}`);
    try {
      const { error } = await supabase
        .from("off_category_tag_canonicals")
        .delete()
        .eq("off_tag", trimmed);
      if (error && error.code !== "42P01") {
        console.warn(`[admin] remove off_tag ${trimmed}:`, error.message);
      }
      setVersion(v => v + 1);
    } finally {
      setBusy(null);
    }
  }

  async function rejectCustom(row) {
    // eslint-disable-next-line no-alert
    const confirm = window.confirm(
      `Reject "${row.name}"?\n\n` +
        `Every pantry_items row with canonical_id = "${row.id}" ` +
        `(${row.count} row${row.count === 1 ? "" : "s"}) will be unlinked, ` +
        `and any ingredient_info stub for this slug will be removed. ` +
        `Can't be undone.`
    );
    if (!confirm) return;

    setBusy(row.id);
    try {
      // Clear the canonical_id on every row first so nothing is left
      // pointing at a rejected slug. Then drop the info stub.
      const { error: clrErr } = await supabase
        .from("pantry_items")
        .update({ canonical_id: null })
        .eq("canonical_id", row.id);
      if (clrErr) throw clrErr;
      const { error: delErr } = await supabase
        .from("ingredient_info")
        .delete()
        .eq("ingredient_id", row.id);
      // 404 on delete is fine — no stub existed.
      if (delErr && delErr.code !== "PGRST116") throw delErr;
      setVersion(v => v + 1); refreshDb?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Reject failed: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) return <Loading />;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r =>
        r.id.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        (r.category || "").toLowerCase().includes(q)
      )
    : rows;
  const customCount   = rows.filter(r => r.source === "custom").length;
  const pendingCount  = rows.filter(r => r.source === "custom" && !r.approved).length;
  const inUse         = rows.filter(r => r.count > 0).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted, letterSpacing: "0.1em", padding: "0 4px 4px" }}>
        {rows.length} CANONICALS · {customCount} CUSTOM · {pendingCount} PENDING · {inUse} IN USE
        {error && <span style={{ color: COLOR.rose, marginLeft: 8 }}>· {error}</span>}
      </div>
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by id, name, category…"
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "10px 12px", marginBottom: 4,
          background: COLOR.deep, border: `1px solid ${COLOR.border}`,
          color: COLOR.ink, borderRadius: 10,
          fontFamily: FONT.sans, fontSize: 13, outline: "none",
        }}
      />
      {filtered.length === 0 ? (
        <Empty msg={`No canonicals match "${search}".`} />
      ) : (
        filtered.map(r => {
          const isBusy = busy === r.id;
          const isCustom = r.source === "custom";
          const statusLabel = !isCustom
            ? "BUNDLED"
            : r.approved ? "APPROVED" : "PENDING";
          const statusColor = !isCustom
            ? COLOR.sub
            : r.approved ? COLOR.leafMuted : COLOR.gold;
          return (
            <div key={r.id} style={{
              padding: "10px 12px",
              background: isCustom && !r.approved ? "#2a2205"
                : isCustom ? COLOR.leafDeep
                : COLOR.ground,
              border: `1px solid ${isCustom && !r.approved ? "#5a4a0a"
                : isCustom ? COLOR.leafDim
                : COLOR.edge}`,
              borderRadius: 10,
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>{r.emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: FONT.serif, fontSize: 14,
                  fontStyle: "italic", color: COLOR.ink,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {r.name}
                </div>
                <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, marginTop: 2, letterSpacing: "0.04em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.id}
                  {r.category && ` · ${r.category.toUpperCase()}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => r.count > 0 && toggleRows(r.id)}
                disabled={r.count === 0}
                title={r.count > 0 ? "View pantry rows + rewire" : "No rows"}
                style={{
                  fontFamily: FONT.mono, fontSize: 9,
                  color: r.count > 0 ? COLOR.leafMuted : COLOR.sub,
                  background: r.count > 0 ? COLOR.leafDeep : "transparent",
                  border: `1px solid ${r.count > 0 ? COLOR.leafDim : COLOR.edge}`,
                  padding: "2px 7px", borderRadius: 10, letterSpacing: "0.08em",
                  flexShrink: 0,
                  cursor: r.count > 0 ? "pointer" : "default",
                }}
              >
                {r.count > 0
                  ? `${rowsFor === r.id ? "▾" : "▸"} ×${r.count}`
                  : "UNUSED"}
              </button>
              <span style={{
                fontFamily: FONT.mono, fontSize: 8,
                color: statusColor, letterSpacing: "0.1em",
                flexShrink: 0,
              }}>
                {statusLabel}
              </span>
              <button
                onClick={() => isCustom ? renameCustom(r) : renameBundled(r)}
                disabled={isBusy}
                style={adminBtnStyle(COLOR.goldDeep, CHIP_TONES.canonical.fg)}
              >
                {isBusy ? "…" : "RENAME"}
              </button>
              {/* PACKAGES button retired — package sizes are now
                  learned from user pantry_items via the
                  popular_package_sizes RPC (migration 0063) and
                  backstopped by AI-generated typicalSizes from
                  ingredient_info.info.package.typicalSizes. Admin
                  curation of the sizes catalog is no longer a
                  concept. */}
              {isCustom && !r.approved && (
                <button
                  onClick={() => approveCustom(r)}
                  disabled={isBusy}
                  style={adminBtnStyle("#0a2a12", COLOR.leafMuted)}
                >
                  {isBusy ? "…" : "APPROVE"}
                </button>
              )}
              {isCustom && (
                <button
                  onClick={() => rejectCustom(r)}
                  disabled={isBusy}
                  style={adminBtnStyle(COLOR.roseDeep, COLOR.rose)}
                >
                  {isBusy ? "…" : "REJECT"}
                </button>
              )}

              {/* GROUP UNDER chip picker (v0.13.0) — lets admin assign
                  any user-created canonical to one of the bundled
                  HUBS so it wraps correctly in the Kitchen tile
                  grouper. Writes to ingredient_info.info.parentId.
                  Bundled canonicals get their parentId from the
                  registry and can't be re-routed — so we hide the
                  picker on bundled rows to avoid confusion. */}
              {isCustom && (
                <div style={{ width: "100%", marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{
                    fontFamily: FONT.mono, fontSize: 8,
                    color: COLOR.sub, letterSpacing: "0.1em",
                    marginRight: 2,
                  }}>
                    GROUP UNDER
                  </span>
                  <button
                    onClick={() => setParent(r, null)}
                    disabled={isBusy || !r.parentId}
                    style={{
                      padding: "3px 7px",
                      background: !r.parentId ? COLOR.soil : "transparent",
                      border: `1px solid ${!r.parentId ? COLOR.border : COLOR.edge}`,
                      color: !r.parentId ? COLOR.dim : COLOR.sub,
                      borderRadius: 10,
                      fontFamily: FONT.mono, fontSize: 8,
                      letterSpacing: "0.05em", cursor: "pointer",
                    }}
                  >
                    — NONE
                  </button>
                  {HUBS.map(hub => {
                    const active = r.parentId === hub.id;
                    return (
                      <button
                        key={hub.id}
                        onClick={() => setParent(r, hub.id)}
                        disabled={isBusy || active}
                        style={{
                          padding: "3px 7px",
                          background: active ? COLOR.goldDeep : "transparent",
                          border: `1px solid ${active ? COLOR.gold : COLOR.edge}`,
                          color: active ? COLOR.gold : COLOR.dim,
                          borderRadius: 10,
                          fontFamily: FONT.mono, fontSize: 8,
                          letterSpacing: "0.05em",
                          cursor: active ? "default" : "pointer",
                        }}
                      >
                        {hub.name.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* OFF TAGS — Tier-1 learned tag map editor. Lists every
                  OFF categoryHint slug currently mapped to this
                  canonical, with an × on each chip to unmap and a
                  "+ TAG" button to bulk-add. Rendered for BOTH
                  bundled and custom canonicals — an admin might want
                  to seed "milks, dairy-milks" onto bundled `milk`
                  just as much as "sodas, colas" onto custom
                  `soft_drink`. Tag chips are tan (canonical color)
                  since OFF tags are literally "what this canonical
                  is" in OFF's taxonomy. */}
              <div style={{ width: "100%", marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{
                  fontFamily: FONT.mono, fontSize: 8,
                  color: COLOR.sub, letterSpacing: "0.1em",
                  marginRight: 2,
                }}>
                  OFF TAGS
                </span>
                {(offTagsByCanonical.get(r.id) || []).map(tag => {
                  const untagBusy = busy === `untag:${tag}`;
                  return (
                    <button
                      key={tag}
                      onClick={() => removeOffTag(tag)}
                      disabled={untagBusy}
                      title={`Remove "${tag}" mapping`}
                      style={{
                        padding: "3px 7px",
                        background: COLOR.goldDeep,
                        border: `1px solid ${COLOR.goldDim}`,
                        color: CHIP_TONES.canonical.fg,
                        borderRadius: 10,
                        fontFamily: FONT.mono, fontSize: 8,
                        letterSpacing: "0.05em",
                        cursor: untagBusy ? "not-allowed" : "pointer",
                        display: "inline-flex", alignItems: "center", gap: 4,
                      }}
                    >
                      <span>{tag}</span>
                      <span style={{ color: CHIP_TONES.canonical.fg }}>×</span>
                    </button>
                  );
                })}
                <button
                  onClick={() => addOffTags(r)}
                  disabled={busy === `tags:${r.id}`}
                  title="Add OFF category tags that should Tier-1 match this canonical"
                  style={{
                    padding: "3px 7px",
                    background: "transparent",
                    border: `1px dashed ${COLOR.goldDim}`,
                    color: CHIP_TONES.canonical.fg,
                    borderRadius: 10,
                    fontFamily: FONT.mono, fontSize: 8,
                    letterSpacing: "0.05em",
                    cursor: busy === `tags:${r.id}` ? "not-allowed" : "pointer",
                  }}
                >
                  {busy === `tags:${r.id}` ? "…" : "+ TAG"}
                </button>
              </div>

              {/* Row drilldown. Clicking the count badge loads the
                  actual pantry_items on this canonical so the admin
                  can rewire a single row to a different (or new)
                  canonical — surgical per-row fix for scan misfires
                  like "SUGAR FREE" Pepsi landing on the sugar slug.
                  Lazy-fetched; renders a spinner-ish state until the
                  query lands. */}
              {rowsFor === r.id && (
                <div style={{
                  width: "100%", marginTop: 6,
                  padding: "6px 0 0", borderTop: `1px dashed ${COLOR.edge}`,
                  display: "flex", flexDirection: "column", gap: 4,
                }}>
                  {!rowsCache.has(r.id) ? (
                    <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, padding: "6px 4px", letterSpacing: "0.08em" }}>
                      LOADING ROWS…
                    </div>
                  ) : (rowsCache.get(r.id) || []).length === 0 ? (
                    <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, padding: "6px 4px", letterSpacing: "0.08em" }}>
                      NO ROWS FOUND (usage count may be stale — refresh)
                    </div>
                  ) : (rowsCache.get(r.id) || []).map(pr => {
                    const rowBusy = busy === `row:${pr.id}`;
                    const amtTxt = pr.amount != null
                      ? `${Number(pr.amount) % 1 ? Number(pr.amount).toFixed(2) : pr.amount}${pr.unit ? ` ${pr.unit}` : ""}`
                      : "";
                    return (
                      <div key={pr.id} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "6px 8px",
                        background: COLOR.deep,
                        border: "1px solid #1f1f1f",
                        borderRadius: 8,
                      }}>
                        <span style={{ fontSize: 16, flexShrink: 0 }}>{pr.emoji || "🥫"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontFamily: FONT.sans, fontSize: 12,
                            color: COLOR.ink,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {pr.brand ? <span style={{ color: CHIP_TONES.canonical.fg }}>{pr.brand} · </span> : null}
                            {pr.name || "(unnamed)"}
                          </div>
                          <div style={{
                            fontFamily: FONT.mono, fontSize: 8,
                            color: COLOR.sub, marginTop: 1, letterSpacing: "0.04em",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {amtTxt}{amtTxt && " · "}{pr.id.slice(0, 8)}
                          </div>
                        </div>
                        <button
                          onClick={() => rewireRow(pr, r.id)}
                          disabled={rowBusy}
                          style={adminBtnStyle("#0a1a2a", COLOR.sky)}
                        >
                          {rowBusy ? "…" : "REWIRE →"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
      <div style={{
        marginTop: 10, padding: "10px 12px",
        background: COLOR.deep, border: `1px dashed ${COLOR.border}`,
        borderRadius: 8,
        fontFamily: FONT.mono, fontSize: 9,
        color: COLOR.muted, lineHeight: 1.6, letterSpacing: "0.04em",
      }}>
        BUNDLED canonicals live in src/data/ingredients.js. RENAME on
        a bundled row writes a display-name override into
        ingredient_info.info.display_name (the slug itself stays put
        since it's referenced in code). CUSTOM canonicals arrive from
        ItemCard's "+ CREATE" flow — APPROVE writes an ingredient_info
        stub so the slug stops reading as PENDING, REJECT clears the
        slug from every pantry_items row and drops the stub, RENAME
        rewrites the slug across the board and carries any approval
        forward. Click the ×N usage badge to drill into the individual
        pantry rows on a canonical and REWIRE a single row to a
        different (or brand-new) canonical — use this when a scan
        misfired on just one row ("SUGAR FREE" Pepsi landing on the
        sugar slug) and you don't want to rewrite every sugar row.
        OFF TAGS teaches the resolver directly: any tag added here
        wins Tier-1 "exact" matching on every fresh scan whose OFF
        categoryHints carry it — one bulk-add of
        "sodas, colas, carbonated-drinks, diet-sodas" onto soft_drink
        and future cold-start soda scans resolve without a correction
        round-trip. Package sizes are no longer admin-curated here
        — they're learned from pantry_items observations
        (popular_package_sizes RPC) with AI-generated typicalSizes as
        the cold-start fallback.
      </div>

      {/* EditPackagingModal removed — admin sizes catalog retired.
          See the PACKAGES-button removal note above for the full
          story. */}
    </div>
  );
}

function adminBtnStyle(bg, fg) {
  return {
    padding: "6px 10px",
    background: bg,
    border: `1px solid ${fg}44`,
    color: fg,
    borderRadius: 6,
    fontFamily: FONT.mono, fontSize: 10,
    letterSpacing: "0.08em", cursor: "pointer", fontWeight: 700,
  };
}

// ── Recipe submissions ────────────────────────────────────────────
// Lists user-authored recipes currently in the review queue
// (submitted_for_review=true AND review_status='pending'). Approve /
// reject flips status and clears submitted_for_review, dropping the
// row out of admin SELECT scope — see migration 0052 for the RLS.
// Approval is intentionally non-destructive: it just stamps the row
// as approved. Actual promotion into src/data/recipes/ is still a
// manual code edit; this queue is the trigger for that work.
function RecipeSubmissionsList({ viewerId }) {
  const { adminList, adminDecide } = useUserRecipes(viewerId);
  const [rows, setRows] = useState(null);        // null = loading
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);        // row id currently being decided
  const [preview, setPreview] = useState(null);  // row being previewed, or null

  const reload = async () => {
    try {
      const data = await adminList();
      setRows(data);
      setError(null);
    } catch (e) {
      console.error("[admin recipes] load failed:", e);
      setError(e.message || "load failed");
      setRows([]);
    }
  };
  // Load once on mount; admins re-open the panel when they want fresh data.
  useEffect(() => { reload(); }, []);

  const decide = async (row, status) => {
    setBusy(row.id);
    try {
      await adminDecide(row.id, { status });
      setRows(prev => (prev || []).filter(r => r.id !== row.id));
      if (preview?.id === row.id) setPreview(null);
    } catch (e) {
      console.error("[admin recipes] decide failed:", e);
      setError(e.message || "update failed");
    } finally {
      setBusy(null);
    }
  };

  if (rows === null) return <Loading />;
  if (error) return <Empty msg={`Load failed: ${error}`} />;
  if (!rows.length) return <Empty msg="No recipes pending review." />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map(row => {
        const r = row.recipe || {};
        return (
          <div key={row.id} style={{
            background: COLOR.deep, border: `1px solid ${COLOR.edge}`, borderRadius: 10,
            padding: "12px 14px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 26, flexShrink: 0 }}>{r.emoji || "🍽️"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: FONT.serif, fontSize: 15, color: COLOR.ink,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {r.title || "(untitled)"}
                </div>
                <div style={{
                  marginTop: 2, fontFamily: FONT.mono, fontSize: 9,
                  color: COLOR.muted, letterSpacing: "0.06em",
                }}>
                  {(r.cuisine || "OTHER").toUpperCase()} · {totalTimeMin(r)} MIN · {difficultyLabel(r.difficulty).toUpperCase()} · SERVES {r.serves || "?"}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button onClick={() => setPreview(row)} style={adminBtnStyle(COLOR.soil, COLOR.dim)}>PREVIEW</button>
              <button
                onClick={() => decide(row, "approved")}
                disabled={busy === row.id}
                style={{ ...adminBtnStyle("#0f2215", COLOR.leaf), opacity: busy === row.id ? 0.5 : 1 }}
              >
                {busy === row.id ? "…" : "APPROVE"}
              </button>
              <button
                onClick={() => decide(row, "rejected")}
                disabled={busy === row.id}
                style={{ ...adminBtnStyle(COLOR.roseDeep, COLOR.rose), opacity: busy === row.id ? 0.5 : 1 }}
              >
                REJECT
              </button>
            </div>
          </div>
        );
      })}

      {preview && <RecipePreviewModal row={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function RecipePreviewModal({ row, onClose }) {
  const r = row.recipe || {};
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 380,
        background: "#000000dd",
        display: "flex", alignItems: "flex-end",
        maxWidth: 480, margin: "0 auto",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", background: COLOR.night,
          borderRadius: "18px 18px 0 0",
          maxHeight: "90vh", overflowY: "auto",
          padding: "18px 20px 28px",
        }}
      >
        <div style={{ width: 44, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 12px" }} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 42 }}>{r.emoji || "🍽️"}</div>
          <div style={{ fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic", color: COLOR.ink, margin: "6px 0 4px" }}>
            {r.title || "(untitled)"}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted, letterSpacing: "0.08em" }}>
            {(r.cuisine || "OTHER").toUpperCase()} · {totalTimeMin(r)} MIN · {difficultyLabel(r.difficulty).toUpperCase()} · SERVES {r.serves || "?"}
          </div>
        </div>
        <div style={{ marginTop: 18, fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.1em" }}>
          INGREDIENTS
        </div>
        <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
          {(r.ingredients || []).map((ing, i) => (
            <li key={i} style={{
              padding: "6px 10px", background: COLOR.soil, borderRadius: 8,
              fontFamily: FONT.sans, fontSize: 12, color: COLOR.ink,
              display: "flex", gap: 8,
            }}>
              <span style={{ color: CHIP_TONES.canonical.fg, fontFamily: FONT.mono, fontSize: 10, minWidth: 52 }}>
                {ing.amount || "—"}
              </span>
              <span>{ing.item}</span>
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 18, fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.1em" }}>
          STEPS
        </div>
        <ol style={{ margin: "8px 0 0", paddingLeft: 20, display: "flex", flexDirection: "column", gap: 8 }}>
          {(r.steps || []).map((s, i) => (
            <li key={i} style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim, lineHeight: 1.5 }}>
              <strong style={{ color: COLOR.ink }}>{s.title}</strong>
              {s.instruction ? <> — {s.instruction}</> : null}
            </li>
          ))}
        </ol>
        <button
          onClick={onClose}
          style={{
            marginTop: 18, width: "100%", padding: "12px",
            background: COLOR.soil, border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 10,
            fontFamily: FONT.mono, fontSize: 11, letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          CLOSE
        </button>
      </div>
    </div>
  );
}

// ── Baseline coverage ─────────────────────────────────────────────
// Visibility into the external-ingest state of barcode_identity_
// corrections: how many UPCs have been seeded from USDA/OFF, how
// many still await a canonical link (admin review queue), and a
// scrollable list of the unlinked rows so an admin can spot
// large-volume blind spots at a glance. Write actions (linking a
// canonical to a row, editing brand, etc.) live in the scan path
// itself — admin scans the UPC, Kitchen.jsx's ItemCard opens with
// the baseline fields pre-filled, the admin picks the canonical,
// and rememberBarcodeCorrection upgrades the row's source_provenance
// entries to "admin". That keeps the BASELINE tab read-only and the
// write surface consistent with the normal scan flow.
function BaselineCoverage() {
  const [counts, setCounts] = useState(null);
  const [queue,  setQueue]  = useState(null);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Four parallel counts: total rows, rows synced from external,
      // rows admin-curated (any field stamped "admin"), rows still
      // awaiting a canonical link. Supabase's head+count keeps these
      // cheap — no row data transferred.
      const results = await Promise.all([
        supabase.from("barcode_identity_corrections").select("id", { count: "exact", head: true }),
        supabase.from("barcode_identity_corrections").select("id", { count: "exact", head: true })
          .not("last_external_sync", "is", null),
        supabase.from("barcode_identity_corrections").select("id", { count: "exact", head: true })
          .not("source_provenance", "eq", "{}"),
        supabase.from("barcode_identity_corrections").select("id", { count: "exact", head: true })
          .is("canonical_id", null),
      ]);
      const err = results.find(r => r.error)?.error;
      if (!alive) return;
      if (err) { setError(err.message); return; }
      setCounts({
        total:        results[0].count || 0,
        externalSynced: results[1].count || 0,
        anyProvenance: results[2].count || 0,
        awaitingCanonical: results[3].count || 0,
      });
      // Top-100 unlinked rows, most-recently-synced first. Gives an
      // admin a visible review queue without paging UI (100 is enough
      // to spot the high-volume misses; deeper drilldown waits on a
      // real need).
      const { data: q, error: qErr } = await supabase
        .from("barcode_identity_corrections")
        .select("id, barcode_upc, brand, name, category_hints, last_external_sync")
        .is("canonical_id", null)
        .not("last_external_sync", "is", null)
        .order("last_external_sync", { ascending: false })
        .limit(100);
      if (!alive) return;
      if (qErr) { setError(qErr.message); return; }
      setQueue(q || []);
    })();
    return () => { alive = false; };
  }, []);

  if (error) return <Empty msg={`Load failed: ${error}`} />;
  if (counts === null || queue === null) return <Loading />;

  const coveragePct = counts.total > 0
    ? Math.round(((counts.total - counts.awaitingCanonical) / counts.total) * 100)
    : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <StatRow label="UPCs seeded (total)"      value={counts.total} />
      <StatRow label="From external baseline"   value={counts.externalSynced} sub={pctOf(counts.externalSynced, counts.total)} />
      <StatRow label="Admin-curated fields"     value={counts.anyProvenance} sub={pctOf(counts.anyProvenance, counts.total)} />
      <StatRow label="Awaiting canonical link"  value={counts.awaitingCanonical} sub={pctOf(counts.awaitingCanonical, counts.total)} />
      <StatRow label="Canonical coverage"       value={`${coveragePct}%`} />

      <div style={{
        marginTop: 4,
        fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted,
        letterSpacing: "0.14em", padding: "0 4px",
      }}>
        REVIEW QUEUE · TOP {queue.length} UNLINKED
      </div>
      {queue.length === 0 ? (
        <Empty msg="No unlinked external-baseline rows — every seeded UPC is linked." />
      ) : (
        queue.map(r => (
          <div
            key={r.id}
            style={{
              display: "flex", flexDirection: "column", gap: 4,
              padding: "10px 12px",
              background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
              borderRadius: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
              <div style={{
                fontFamily: FONT.serif, fontStyle: "italic", fontSize: 14,
                color: COLOR.ink,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {r.name || <span style={{ color: COLOR.muted }}>unnamed</span>}
              </div>
              <div style={{
                fontFamily: FONT.mono, fontSize: 10, color: CHIP_TONES.canonical.fg,
                whiteSpace: "nowrap",
              }}>
                {r.brand || "—"}
              </div>
            </div>
            <div style={{
              fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted,
              letterSpacing: "0.08em",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              UPC {r.barcode_upc}
              {r.category_hints && r.category_hints.length > 0 && (
                <> · {r.category_hints.slice(0, 3).join(" · ")}</>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ── Shared loading / empty ────────────────────────────────────────
function Loading() {
  return (
    <div style={{ padding: "40px 12px", textAlign: "center", fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, letterSpacing: "0.1em" }}>
      LOADING…
    </div>
  );
}
function Empty({ msg }) {
  return (
    <div style={{
      padding: "28px 16px", textAlign: "center",
      background: COLOR.deep, border: `1px dashed ${COLOR.edge}`, borderRadius: 10,
      fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, fontStyle: "italic",
    }}>
      {msg}
    </div>
  );
}

// XP economy observability panel. Calls the xp_economy_stats RPC
// (admin-gated, SECURITY DEFINER) and renders the telemetry from
// §7 of the XP plan: median/P90 per-user XP, cap-hit counts,
// streak histogram, curated share, revival usage, gate-blocked
// XP, median time to L10.
function XpEconomyStats() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error,   setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error: e } = await supabase.rpc("xp_economy_stats");
      if (!alive) return;
      if (e) { setError(e); setStats(null); }
      else   { setStats(data || {}); setError(null); }
      setLoad(false);
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div style={statusStyle}>Loading XP stats…</div>;
  if (error)   return <div style={statusStyle}>Load failed: {error.message}</div>;
  if (!stats)  return <div style={statusStyle}>No stats returned.</div>;

  const hist = stats.streak_length_histogram || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <StatRow label="Active users (7d)"        value={stats.active_users_last_7} />
      <StatRow label="Median XP/user/day (28d)" value={stats.median_xp_per_user_day} />
      <StatRow label="P90 XP/user/day (28d)"    value={stats.p90_xp_per_user_day} />
      <StatRow label="Events (30d)"             value={stats.total_events_30} />
      <StatRow label="Cap-trimmed events (30d)" value={stats.cap_hit_count_30} sub={pctOf(stats.cap_hit_count_30, stats.total_events_30)} />
      <StatRow label="Hard-capped zeros (30d)"  value={stats.hard_cap_zeroed_count_30} sub={pctOf(stats.hard_cap_zeroed_count_30, stats.total_events_30)} />
      <StatRow label="Curated share (30d)"      value={`${stats.curated_share_pct}%`} />
      <StatRow label="Revivals (30d)"           value={stats.revival_usage_30} />
      <StatRow label="XP blocked by gates (30d)" value={stats.xp_blocked_by_gates_30} />
      <StatRow label="Median days to L10"       value={stats.median_days_to_l10} />

      <div style={{
        marginTop: 4, background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
        borderRadius: 10, padding: "12px 14px",
      }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted,
          letterSpacing: "0.14em", marginBottom: 8,
        }}>
          STREAK LENGTH · CURRENT
        </div>
        {["0", "1-2", "3-6", "7-13", "14-29", "30+"].map(b => (
          <div key={b} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <div style={{ width: 44, fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>{b}</div>
            <div style={{ flex: 1, background: COLOR.ground, borderRadius: 4, height: 6, overflow: "hidden" }}>
              <div style={{
                height: "100%", background: CHIP_TONES.category.fg,
                width: `${pctBar(hist[b] || 0, hist)}%`,
                transition: "width 240ms ease-out",
              }} />
            </div>
            <div style={{ width: 34, textAlign: "right", fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>
              {hist[b] || 0}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatRow({ label, value, sub }) {
  return (
    <div style={{
      background: COLOR.deep, border: `1px solid ${COLOR.edge}`, borderRadius: 10,
      padding: "10px 14px", display: "flex", alignItems: "baseline", justifyContent: "space-between",
    }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim, letterSpacing: "0.08em" }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontFamily: FONT.mono, fontSize: 16, color: COLOR.gold, fontWeight: 500 }}>
          {value ?? "—"}
        </span>
        {sub && (
          <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.muted }}>
            {sub}
          </span>
        )}
      </div>
    </div>
  );
}

function pctOf(n, d) {
  if (!d || d === 0) return "";
  return `(${Math.round((n / d) * 100)}%)`;
}
function pctBar(value, hist) {
  const max = Math.max(1, ...Object.values(hist || {}).map(Number));
  return Math.round((Number(value || 0) / max) * 100);
}

const statusStyle = {
  padding: "28px 16px", textAlign: "center",
  background: COLOR.deep, border: `1px dashed ${COLOR.edge}`, borderRadius: 10,
  fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, fontStyle: "italic",
};

// ── Weather FX preview ────────────────────────────────────────────
// Temporary admin tool — forces the WeatherFX overlay into a chosen
// preset so the visual can be validated on a sunny day. localStorage-
// backed so it survives reloads but is per-device. "Use real weather"
// clears the override and falls back to the live useWeather() reading.
function WeatherFxControls() {
  const [override, setOverrideState] = useState(() => getWeatherFxOverride());
  const apply = (value) => {
    setWeatherFxOverride(value);
    setOverrideState(value);
  };
  const PRESETS = [
    { id: null,        label: "Use real weather", hint: "Falls through to useWeather()" },
    { id: "cloudy",    label: "Cloudy",           hint: "5 drifting cloud puffs" },
    { id: "drizzle",   label: "Drizzle",          hint: "35 light streaks" },
    { id: "rain",      label: "Rain",             hint: "70 streaks at full pace" },
    { id: "storm",     label: "Storm",            hint: "110 streaks + lightning" },
    { id: "snow",      label: "Snow",             hint: "50 drifting flakes" },
    { id: "clear",     label: "Clear (off)",      hint: "Renders nothing" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 10, color: COLOR.muted,
        letterSpacing: "0.12em",
      }}>
        OVERLAY PRESET — local only, persists in localStorage
      </div>
      {PRESETS.map(p => {
        const active = (override || null) === (p.id || null);
        return (
          <button
            key={p.id || "none"}
            onClick={() => apply(p.id)}
            style={{
              display: "flex", flexDirection: "column",
              alignItems: "flex-start", gap: 2,
              padding: "10px 14px",
              background: active ? COLOR.roseDeep : COLOR.deep,
              border: `1px solid ${active ? COLOR.roseDim : COLOR.edge}`,
              borderRadius: 10,
              cursor: "pointer", textAlign: "left",
            }}
          >
            <span style={{
              fontFamily: FONT.sans, fontSize: 14, fontWeight: 600,
              color: active ? COLOR.rose : COLOR.ink,
            }}>
              {p.label}{active ? "  ·  active" : ""}
            </span>
            <span style={{
              fontFamily: FONT.mono, fontSize: 10, color: COLOR.muted,
              letterSpacing: "0.06em",
            }}>
              {p.hint}
            </span>
          </button>
        );
      })}
      <div style={{
        marginTop: 8, padding: 10,
        background: COLOR.deep, border: `1px dashed ${COLOR.edge}`,
        borderRadius: 8,
        fontFamily: FONT.sans, fontSize: 11, color: COLOR.muted,
        fontStyle: "italic", lineHeight: 1.5,
      }}>
        Override is per-device. Pick "Use real weather" to clear it once
        the WeatherFX feature ships and the admin override is no longer
        needed for testing.
      </div>
    </div>
  );
}

// ── Voicelines panel ─────────────────────────────────────────────
//
// Bulk-bake the cached cooking voicelines per voice. Each (voice ×
// phrase) pair produces one MP3 in Storage at
// recipe-audio/voicelines/<voiceId>/<phraseKey>.mp3. Idempotent —
// already-cached pairs return the cached URL without re-paying
// ElevenLabs unless `force` is on.
//
// Cost: ~$0.05-0.10 per ElevenLabs call on the Pro tier; the full
// bake is voices × phrases (currently 9 × 6 = 54). Re-bake with
// `force` only when phrase texts change.
function VoicelinesPanel() {
  // Which voice's drawer is currently open. Only one at a time so
  // the panel doesn't sprawl. Defaults to the first voice (Italian)
  // since the user mentioned testing on that one first.
  const [openVoiceSlug, setOpenVoiceSlug] = useState(CONFIGURED_VOICES[0]?.slug || null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted,
        letterSpacing: "0.10em", padding: "0 4px 4px",
      }}>
        {CONFIGURED_VOICES.length} VOICES · {VOICELINE_KEYS.length} PHRASES · TAP A VOICE TO EXPAND
      </div>
      {CONFIGURED_VOICES.map((v) => (
        <VoiceAccordion
          key={v.voiceId}
          voice={v}
          open={openVoiceSlug === v.slug}
          onToggle={() => setOpenVoiceSlug((prev) => prev === v.slug ? null : v.slug)}
        />
      ))}
    </div>
  );
}

// Per-voice accordion: header with voice name + chevron, expanded
// body with one editable text field per phrase + a per-phrase BAKE
// button. Custom text persists in localStorage so re-baking later
// doesn't require re-typing.
function VoiceAccordion({ voice, open, onToggle }) {
  return (
    <div style={{
      background: COLOR.soil,
      border: `1px solid ${COLOR.border}`,
      borderRadius: 10,
      overflow: "hidden",
    }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", width: "100%",
          gap: 10, padding: "10px 12px",
          background: "transparent", border: "none",
          textAlign: "left", cursor: "pointer",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.ink }}>
            {voice.label}
          </div>
          <div style={{
            fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub,
            letterSpacing: "0.04em",
          }}>
            {voice.slug} · {voice.voiceId}
          </div>
        </div>
        <span style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted }}>
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div style={{
          padding: "0 12px 12px",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {VOICELINE_KEYS.map((phraseKey) => (
            <PhraseRow
              key={phraseKey}
              voice={voice}
              phraseKey={phraseKey}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PhraseRow({ voice, phraseKey }) {
  // localStorage key — survives across page reloads so a custom
  // text re-bake doesn't require retyping.
  const storageKey = `voiceline_override:${voice.slug}:${phraseKey}`;
  const [text, setText] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored !== null) return stored;
    } catch { /* swallow */ }
    return resolveVoicelineText(phraseKey, voice.slug) || "";
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // "generated" | "cached" | "failed"
  const [errMsg, setErrMsg] = useState("");

  // Persist edits to localStorage on change so a refresh restores
  // them. Storing under-the-hood; never ride this through the bake
  // unless the user types something different from the default
  // (handled at bake time below).
  const updateText = (next) => {
    setText(next);
    try { window.localStorage.setItem(storageKey, next); }
    catch { /* private mode / quota */ }
  };

  const defaultText = resolveVoicelineText(phraseKey, voice.slug) || "";
  const isOverride = text.trim() && text.trim() !== defaultText.trim();

  const fire = async () => {
    if (busy) return;
    setBusy(true);
    setErrMsg("");
    setResult(null);
    try {
      const res = await generateVoiceline({
        voiceId: voice.voiceId,
        voiceSlug: voice.slug,
        phraseKey,
        // Only pass customText when it differs from the resolved
        // default — otherwise the catalog lookup runs server-side
        // and produces the same audio at lower coordination overhead.
        customText: isOverride ? text.trim() : undefined,
        force: isOverride,    // custom text always re-bakes
      });
      // Stamp the bake timestamp into localStorage so voicelineUrl()
      // appends `?v=<stamp>` and the admin's next playback (this
      // device, this browser) plays the freshly-baked audio instead
      // of a cached older version. The 60s server-side cacheControl
      // handles cross-device propagation; this handles same-device
      // immediacy. Skip on cached-no-op responses — the file didn't
      // change, so don't bust the cache.
      if (!res?.cached && typeof localStorage !== "undefined") {
        try {
          localStorage.setItem(`voiceline_v:${voice.voiceId}:${phraseKey}`, String(Date.now()));
        } catch { /* swallow quota errors */ }
      }
      setResult(res?.cached ? "cached" : "generated");
    } catch (e) {
      setResult("failed");
      setErrMsg(String(e?.message || e));
    }
    setBusy(false);
  };

  const resetToDefault = () => {
    updateText(defaultText);
    try { window.localStorage.removeItem(storageKey); } catch { /* swallow */ }
  };

  const resultColor = result === "generated" ? COLOR.leaf
    : result === "cached" ? COLOR.gold
    : result === "failed" ? COLOR.rose
    : COLOR.dim;

  return (
    <div style={{
      padding: "10px 12px",
      background: COLOR.deep,
      border: `1px solid ${COLOR.edge}`,
      borderRadius: 8,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
          color: COLOR.gold, letterSpacing: "0.10em",
        }}>
          {phraseKey}
        </span>
        {isOverride && (
          <span style={{
            fontFamily: FONT.mono, fontSize: 8, fontWeight: 600,
            color: COLOR.rose, letterSpacing: "0.10em",
            background: "rgba(180,60,40,0.10)",
            padding: "1px 6px", borderRadius: 6,
          }}>
            CUSTOM
          </span>
        )}
        {result && (
          <span style={{
            fontFamily: FONT.mono, fontSize: 8, fontWeight: 600,
            color: resultColor, letterSpacing: "0.10em",
            marginLeft: "auto",
          }}>
            {result.toUpperCase()}
          </span>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => updateText(e.target.value)}
        rows={2}
        disabled={busy}
        placeholder={defaultText}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "8px 10px",
          background: "rgba(255,252,241,0.65)",
          border: `1px solid ${isOverride ? "rgba(218,165,32,0.4)" : COLOR.edge}`,
          borderRadius: 6,
          fontFamily: FONT.forma, fontSize: 12, lineHeight: 1.4,
          color: COLOR.ink, resize: "vertical",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={fire}
          disabled={busy || !text.trim()}
          style={{
            flex: 1, padding: "6px 10px",
            background: busy ? COLOR.border : COLOR.gold,
            color: busy ? COLOR.dim : "#2a1a05",
            border: "none", borderRadius: 6,
            fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
            letterSpacing: "0.10em",
            cursor: busy ? "wait" : "pointer",
          }}
        >
          {busy ? "BAKING…" : "✨ BAKE"}
        </button>
        {isOverride && (
          <button
            type="button"
            onClick={resetToDefault}
            disabled={busy}
            style={{
              padding: "6px 10px",
              background: "transparent",
              border: `1px solid ${COLOR.border}`,
              color: COLOR.dim, borderRadius: 6,
              fontFamily: FONT.mono, fontSize: 10, fontWeight: 600,
              letterSpacing: "0.10em",
              cursor: busy ? "default" : "pointer",
            }}
          >
            ↺ RESET
          </button>
        )}
      </div>
      {errMsg && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 11, color: COLOR.rose,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {errMsg}
        </div>
      )}
    </div>
  );
}

// ── BG Music tester ──────────────────────────────────────────────
// Admin-only audio playground for the background-music scheduler.
// Each authored hour gets a button; tapping it dispatches a
// `bgMusic:testHour` event that BackgroundMusic listens for and
// force-schedules the intro→loop→outro envelope of that hour at
// the upcoming hour boundary — same code path the wall-clock
// scheduler uses, so what you hear is what users hear at that hour.
// "Live" snaps back to the real wall-clock band (or the peppermint
// fallback when the current hour is unauthored).
//
// Note: the outro is timed to END at the next hour boundary minus a
// tiny safety margin, so triggering "10 AM" at 14:30 plays the
// intro and loops until ~15:00 then outroes. Useful for hearing how
// the seam between an authored hour and the fallback / next hour
// sounds without waiting all day.
function BgMusicTester() {
  const HOURS = [
    { hour: 8,  label: "8 AM"  },
    { hour: 10, label: "10 AM" },
    { hour: 16, label: "4 PM"  },
    { hour: 18, label: "6 PM"  },
    { hour: 21, label: "9 PM"  },
    { hour: 23, label: "11 PM" },
  ];
  const fire = (hour) => {
    window.dispatchEvent(new CustomEvent("bgMusic:testHour", { detail: { hour } }));
  };
  return (
    <div style={{
      padding: "16px", background: COLOR.soil,
      border: `1px solid ${COLOR.border}`, borderRadius: 14,
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 9, color: COLOR.gold,
        letterSpacing: "0.12em",
      }}>
        BACKGROUND MUSIC — TEST HOURS
      </div>
      <div style={{
        fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim,
        lineHeight: 1.5,
      }}>
        Tap an authored hour to force-schedule its intro → loop → outro
        right now (outro times to the next wall-clock hour boundary so
        you can hear the full envelope + handoff). LIVE returns to the
        real-time schedule.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {HOURS.map((h) => (
          <button
            key={h.hour}
            type="button"
            onClick={() => fire(h.hour)}
            style={{
              padding: "10px 16px",
              background: COLOR.deep,
              border: `1px solid ${COLOR.edge}`,
              borderRadius: 10,
              color: COLOR.ink,
              fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
              letterSpacing: "0.10em",
              cursor: "pointer",
            }}
          >
            ▶ {h.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => fire("live")}
          style={{
            padding: "10px 16px",
            background: COLOR.leaf,
            border: "none",
            borderRadius: 10,
            color: "#FFFFFF",
            fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
            letterSpacing: "0.10em",
            cursor: "pointer",
          }}
        >
          ◉ LIVE
        </button>
      </div>
      <div style={{
        fontFamily: FONT.forma, fontSize: 10, color: COLOR.muted,
        marginTop: 4, lineHeight: 1.5,
      }}>
        Heads up: the audio engine only runs after a user gesture has
        unlocked it. If nothing plays, scroll around or tap a button
        elsewhere on the page first. Hours not in this list (9 AM, 11
        AM, 12 PM, 1 PM, 2 PM, 3 PM, 5 PM, 7 PM, 8 PM, 10 PM) fall
        back to peppermint lanes.
      </div>
    </div>
  );
}
